import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { VehicleSocket } from "../../../../../../store/admin/vehiclesSocketStore";
import type { TripTrackingEvent } from "../../../../../../socket";
import { useEscapeKey } from "../../../../../../hooks/useEscapeKey";

interface TripHistoryModalProps {
  vehicle: VehicleSocket;
  events: TripTrackingEvent[];
  isDarkMode: boolean;
  onClose: () => void;
}

const ITEMS_PER_PAGE = 10;

const EVENT_ICONS: Record<string, string> = {
  VIAJE_INICIADO: "ri-play-circle-line text-blue-500",
  INICIO_TARDIO: "ri-timer-line text-yellow-500",
  DEMORA_EN_INICIO: "ri-time-line text-yellow-500",
  DESVIO_DE_RUTA: "ri-alert-line text-red-500",
  SIGUE_FUERA_DE_RUTA: "ri-alert-line text-red-400",
  REGRESO_A_RUTA: "ri-arrow-go-back-line text-green-500",
  VIAJE_COMPLETADO: "ri-checkbox-circle-line text-green-500",
};

export default function TripHistoryModal({
  vehicle,
  events,
  isDarkMode,
  onClose,
}: TripHistoryModalProps) {
  useEscapeKey(onClose);
  const { t } = useTranslation("lite");
  const [currentPage, setCurrentPage] = useState(1);

  const totalItems = events.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));

  const paginatedEvents = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return events.slice(start, start + ITEMS_PER_PAGE);
  }, [events, currentPage]);

  const bgColor = isDarkMode ? "bg-dark-bg" : "bg-white";
  const textColor = isDarkMode ? "text-gray-300" : "text-gray-700";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const headerBg = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const rowHoverBg = isDarkMode ? "hover:bg-[#252525]" : "hover:bg-gray-50";
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";

  const formatTime = (ts: number) => new Date(ts).toLocaleTimeString();
  const formatDate = (ts: number) => new Date(ts).toLocaleDateString();

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div
        className={`${bgColor} rounded-xl max-w-3xl w-full max-h-[85vh] overflow-hidden border ${borderColor} shadow-2xl flex flex-col`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${borderColor}`}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-route-line text-black text-lg"></i>
            </div>
            <div>
              <h2 className={`font-semibold text-lg ${textColor}`}>
                {t("tripHistoryModal.title", "Historial de viaje")}
              </h2>
              <p className={`text-xs ${textSecondary}`}>
                {vehicle.vehicle?.name || vehicle.imei || vehicle.id} •{" "}
                {t("tripHistoryModal.eventsCount", {
                  count: totalItems,
                  defaultValue: "{{count}} eventos",
                })}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} rounded-lg transition-colors`}
          >
            <i className={`ri-close-line text-xl ${textColor}`}></i>
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {totalItems === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <i className={`ri-route-line text-4xl ${textSecondary} mb-3`}></i>
              <p className={`text-sm ${textSecondary}`}>
                {t("tripHistoryModal.empty", "Sin eventos de viaje")}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className={`${headerBg} sticky top-0 z-10`}>
                <tr className={`${textSecondary} text-xs uppercase`}>
                  <th className="px-4 py-2 text-left font-medium">
                    {t("tripHistoryModal.event", "Evento")}
                  </th>
                  <th className="px-4 py-2 text-left font-medium">
                    {t("tripHistoryModal.message", "Mensaje")}
                  </th>
                  <th className="px-4 py-2 text-left font-medium">
                    {t("tripHistoryModal.date", "Fecha")}
                  </th>
                  <th className="px-4 py-2 text-left font-medium">
                    {t("tripHistoryModal.time", "Hora")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedEvents.map((ev, i) => (
                  <tr
                    key={`${ev.timestamp}-${i}`}
                    className={`border-t ${borderColor} ${rowHoverBg} transition-colors`}
                  >
                    <td className={`px-4 py-2 ${textColor}`}>
                      <div className="flex items-center gap-2">
                        <i
                          className={`text-sm ${EVENT_ICONS[ev.event_type] || "ri-circle-line text-gray-400"}`}
                        ></i>
                        <span className="text-xs font-medium">{ev.event_type}</span>
                      </div>
                    </td>
                    <td className={`px-4 py-2 text-xs ${textSecondary}`}>
                      {ev.message || "-"}
                    </td>
                    <td className={`px-4 py-2 text-xs ${textColor}`}>
                      {formatDate(ev.timestamp)}
                    </td>
                    <td className={`px-4 py-2 text-xs ${textColor}`}>
                      {formatTime(ev.timestamp)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalItems > ITEMS_PER_PAGE && (
          <div className={`flex items-center justify-between p-3 border-t ${borderColor}`}>
            <span className={`text-xs ${textSecondary}`}>
              {t("tripHistoryModal.page", {
                current: currentPage,
                total: totalPages,
                defaultValue: "Página {{current}} de {{total}}",
              })}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded border ${borderColor} ${textColor} text-xs disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 rounded border ${borderColor} ${textColor} text-xs disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
